import type { useI18n } from '@affine/i18n';

type I18n = ReturnType<typeof useI18n>;

type CalendarDeliveryIssue = {
  method?: string;
  reason?: string;
  recipients?: string[];
  rejected?: string[];
  status?: string;
};

const CALENDAR_DELIVERY_REASON_KEYS = {
  credential_unavailable:
    'com.streamlain.calendar.delivery.reason.credential-unavailable',
  feature_unavailable:
    'com.streamlain.calendar.delivery.reason.feature-unavailable',
  mail_account_ineligible:
    'com.streamlain.calendar.delivery.reason.mail-account-ineligible',
  mail_account_missing:
    'com.streamlain.calendar.delivery.reason.mail-account-missing',
  mail_send_failed: 'com.streamlain.calendar.delivery.reason.send-failed',
  recipient_rejected:
    'com.streamlain.calendar.delivery.reason.recipient-rejected',
} as const satisfies Record<string, keyof I18n>;

const isKnownReason = (
  reason: string
): reason is keyof typeof CALENDAR_DELIVERY_REASON_KEYS =>
  Object.hasOwn(CALENDAR_DELIVERY_REASON_KEYS, reason);

export const calendarDeliveryIssueText = (
  t: I18n,
  issues: CalendarDeliveryIssue[]
) => {
  const recipients = [
    ...new Set(
      issues.flatMap(issue =>
        [...(issue.rejected ?? []), ...(issue.recipients ?? [])].filter(Boolean)
      )
    ),
  ];
  const reasons = [
    ...new Set(issues.map(issue => issue.reason).filter(Boolean)),
  ];
  return [
    t['com.streamlain.calendar.delivery.issue'](),
    recipients.join(', '),
    reasons.map(reason => formatCalendarDeliveryReason(t, reason)).join(', '),
  ]
    .filter(Boolean)
    .join(' · ');
};

export const formatCalendarDeliveryReason = (
  t: I18n,
  reason: string | undefined
) =>
  reason && isKnownReason(reason)
    ? t[CALENDAR_DELIVERY_REASON_KEYS[reason]]()
    : t['com.streamlain.calendar.delivery.reason.unknown']();
